import Reveal from "@/components/Reveal";
import { ProspectusFigure } from "@/components/ProspectusFigure";
import {
  PROSPECTUS_META as META,
  PROSPECTUS_IMAGES as IMAGES,
  PROSPECTUS_SECTIONS as SECTIONS,
  PROSPECTUS_TIMELINE as TIMELINE,
  PROSPECTUS_COLLECTION as COLLECTION,
  PROSPECTUS_HERITAGE_THEMES as HERITAGE_THEMES,
  PROSPECTUS_FILMS as FILMS,
} from "@/lib/prospectus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PullQuote = ({ text, attribution }) => (
  <blockquote className="my-14 md:my-20 max-w-3xl">
    <div className="rule mb-8 max-w-[80px]" />
    <p className="font-serif italic text-2xl md:text-4xl leading-[1.25]">
      &ldquo;{text}&rdquo;
    </p>
    {attribution && <p className="hairline opacity-50 mt-6">{attribution}</p>}
    <div className="rule mt-8 max-w-[80px]" />
  </blockquote>
);

const Timeline = () => (
  <div className="my-14 md:my-20 max-w-3xl" data-testid="prospectus-timeline">
    {TIMELINE.map((t, i) => (
      <div
        key={i}
        className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-8 py-6 border-t border-[color:var(--rule)]"
      >
        <p className="sm:col-span-4 font-serif italic text-xl md:text-2xl leading-tight">
          {t.year}
        </p>
        <p className="sm:col-span-8 font-serif text-base md:text-lg leading-[1.55] opacity-85">
          {t.text}
        </p>
      </div>
    ))}
    <div className="rule" />
  </div>
);

const CollectionCards = () => (
  <div
    className="my-14 md:my-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-[color:var(--rule)] border border-[color:var(--rule)]"
    data-testid="prospectus-collection-cards"
  >
    {COLLECTION.map((c, i) => (
      <div key={i} className="bg-[color:var(--bone)] p-8 md:p-10">
        <p className="hairline opacity-50 mb-4">
          {String(i + 1).padStart(2, "0")}
        </p>
        <h3 className="font-serif italic text-2xl md:text-3xl leading-tight">
          {c.title}
        </h3>
        <p className="font-serif text-base md:text-lg leading-[1.55] opacity-80 mt-4">
          {c.body}
        </p>
      </div>
    ))}
  </div>
);

const Films = () => (
  <div className="my-14 md:my-20 space-y-12 max-w-3xl" data-testid="prospectus-films">
    {FILMS.map((f, i) => (
      <div key={i}>
        <p className="hairline opacity-50 mb-4">Film · {String(i + 1).padStart(2, "0")}</p>
        <div className="aspect-video w-full bg-black">
          <iframe
            src={f.embed}
            title={f.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <p className="font-serif italic text-lg md:text-xl mt-4">{f.title}</p>
        <p className="font-serif text-base opacity-70 mt-1 leading-[1.5]">{f.note}</p>
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          className="link-quiet font-serif italic text-base mt-3 inline-block"
          data-testid={`prospectus-film-link-${i}`}
        >
          Watch on YouTube →
        </a>
      </div>
    ))}
  </div>
);

const Contact = () => (
  <div className="my-10 max-w-3xl space-y-5" data-testid="prospectus-contact">
    {[
      ["Enquiries", META.contactEmail, `mailto:${META.contactEmail}`],
      ["Website", META.website, `https://www.${META.website}`],
      ["Chapter II", META.chapterSite, `https://${META.chapterSite}`],
      ["Project platform", META.platform, `https://www.${META.platform}`],
      ["Instagram", META.instagram, null],
    ].map(([label, value, href]) => (
      <div key={label} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-8 py-4 border-t border-[color:var(--rule)]">
        <p className="sm:col-span-4 hairline opacity-50 pt-1.5">{label}</p>
        {href ? (
          <a href={href} className="sm:col-span-8 link-quiet font-serif italic text-lg md:text-xl w-fit">
            {value}
          </a>
        ) : (
          <p className="sm:col-span-8 font-serif italic text-lg md:text-xl">{value}</p>
        )}
      </div>
    ))}
    <div className="rule" />
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return (
        <p className="font-serif text-lg md:text-xl leading-[1.6] opacity-90 max-w-[64ch] mb-7">
          {block.text}
        </p>
      );
    case "pull":
      return <PullQuote text={block.text} attribution={block.attribution} />;
    case "figure":
      return <ProspectusFigure image={IMAGES[block.image]} full={block.full} />;
    case "list": {
      const items = block.items === "HERITAGE_THEMES" ? HERITAGE_THEMES : block.items;
      return (
        <div className="my-10 max-w-3xl">
          {block.intro && (
            <p className="font-serif text-lg md:text-xl leading-[1.6] opacity-90 mb-6">
              {block.intro}
            </p>
          )}
          <ul className="space-y-4">
            {items.map((item, i) => (
              <li key={i} className="grid grid-cols-[2rem_1fr] gap-3 border-t border-[color:var(--rule)] pt-4">
                <span className="hairline opacity-40 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-base md:text-lg leading-[1.55] opacity-85">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    case "timeline":
      return <Timeline />;
    case "cards":
      return <CollectionCards />;
    case "films":
      return <Films />;
    case "contact":
      return <Contact />;
    default:
      return null;
  }
};

export default function ArchiveProspectus() {
  return (
    <div data-testid="prospectus-page" className="bg-[color:var(--bone)] text-[color:var(--ink)]">
      {/* Slim institutional top bar */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[color:var(--ink)] text-[color:var(--bone)]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-14 flex items-center justify-between gap-4">
          <a href="#cover" className="hairline opacity-90 truncate">
            The Nick Fraser Archive · Estate Publication
          </a>
          <a
            href={`${API}/press/prospectus.pdf`}
            target="_blank"
            rel="noreferrer"
            className="hairline border border-[color:var(--bone)] px-4 py-2 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500 whitespace-nowrap"
            data-testid="prospectus-pdf-download-top"
          >
            Download PDF →
          </a>
        </div>
      </header>

      {/* Cover */}
      <section
        id="cover"
        data-testid="prospectus-cover"
        className="dark-section min-h-[100svh] flex items-end pt-32 pb-16 md:pb-24"
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="hairline opacity-60 mb-8">
              {META.publisher} · {META.edition} · {META.version}
            </p>
            <h1 className="font-wordmark text-[16vw] md:text-[9vw] lg:text-[7.5rem] leading-[0.95]">
              The Nick Fraser Archive
            </h1>
            <p className="font-serif italic text-xl md:text-3xl opacity-85 mt-8 max-w-[38ch] leading-[1.35]">
              {META.subtitle}
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <a
                href={`${API}/press/prospectus.pdf`}
                target="_blank"
                rel="noreferrer"
                className="hairline border border-[color:var(--bone)] px-6 py-3 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                data-testid="prospectus-pdf-download-cover"
              >
                Download the Archive Prospectus →
              </a>
              <a href="#contents" className="hairline opacity-70 hover:opacity-100 transition-opacity">
                Contents ↓
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] border border-dashed border-[rgba(247,245,240,0.4)] flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className="font-mono text-xs md:text-sm tracking-[0.2em] opacity-70">
                [ COVER_ARTWORK ]
              </span>
              <span className="font-serif italic text-sm opacity-50 max-w-[36ch]">
                {IMAGES.COVER_ARTWORK.note}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Contents */}
      <section id="contents" className="bone-section py-24 md:py-36 scroll-mt-14" data-testid="prospectus-contents">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <p className="hairline opacity-60 mb-10">Contents</p>
          <nav className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="group flex items-baseline gap-6 py-4 border-t border-[color:var(--rule)]"
                data-testid={`prospectus-toc-${s.id}`}
              >
                <span className="hairline opacity-40">{s.num}</span>
                <span className="link-quiet font-serif italic text-xl md:text-2xl">
                  {s.navLabel}
                </span>
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Sections */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {SECTIONS.map((s) => (
          <section
            key={s.id}
            id={s.id}
            className="py-20 md:py-32 border-t border-[color:var(--rule)] scroll-mt-14"
            data-testid={`prospectus-section-${s.id}`}
          >
            <Reveal slow>
              <p className="hairline opacity-50 mb-6">
                {s.num} · {s.navLabel}
              </p>
              <h2 className="font-serif italic text-4xl md:text-6xl leading-[1.05] max-w-[22ch]">
                {s.title}
              </h2>
              {s.draft && (
                <p className="font-mono text-xs mt-6 inline-block border border-[color:var(--oxblood)] text-[color:var(--oxblood)] px-3 py-1.5 tracking-[0.15em]">
                  DRAFT — AWAITING MARCUS&apos;S APPROVAL
                </p>
              )}
              {s.lede && (
                <p className="font-serif italic text-xl md:text-2xl opacity-75 mt-8 max-w-[52ch] leading-[1.45]">
                  {s.lede}
                </p>
              )}
            </Reveal>
            <div className="mt-12 md:mt-16">
              {s.blocks.map((b, i) => (
                <Reveal key={i} slow>
                  <Block block={b} />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Colophon */}
      <footer className="dark-section py-20 md:py-28" data-testid="prospectus-colophon">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <div>
            <p className="font-wordmark text-4xl md:text-5xl">The Nick Fraser Archive</p>
            <p className="hairline opacity-60 mt-4">
              {META.publisher} · {META.edition} · {META.version}
            </p>
            <p className="font-serif italic text-base opacity-60 mt-2">
              Unlisted publication — shared by direct link only.
            </p>
          </div>
          <a
            href={`${API}/press/prospectus.pdf`}
            target="_blank"
            rel="noreferrer"
            className="hairline border border-[color:var(--bone)] px-6 py-3 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500 w-fit"
            data-testid="prospectus-pdf-download-footer"
          >
            Download the Archive Prospectus →
          </a>
        </div>
      </footer>
    </div>
  );
}
